<template>
  <div class="conversation--container" :class="colorSchemeClass">
    <div class="conversation-wrap" :class="{ 'is-typing': isAgentTyping }">
      <div v-if="isFetchingList" class="message--loader">
        <spinner />
      </div>
      <div
        v-for="groupedMessage in groupedMessages"
        :key="groupedMessage.date"
        class="messages-wrap"
      >
        <ChatDateSeparator :date="groupedMessage.date" />

        <transition-group name="message-slide-up" tag="div" appear>
          <chat-message
            v-for="message in groupedMessage.messages"
            v-show="message.status !== 'in_progress'"
            :key="message.id"
            :message="message"
          />
        </transition-group>
      </div>
      <agent-typing-bubble v-if="showStatusIndicator" />
    </div>
  </div>
</template>

<script>
import ChatMessage from 'widget/components/ChatMessage.vue';
import AgentTypingBubble from 'widget/components/AgentTypingBubble.vue';
import ChatDateSeparator from 'widget/v2/components/message-parts/ChatDateSeparator.vue';
import Spinner from 'shared/components/Spinner.vue';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ConversationWrap',
  components: {
    ChatMessage,
    AgentTypingBubble,
    ChatDateSeparator,
    Spinner,
  },
  mixins: [darkModeMixin],
  props: {
    groupedMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previousScrollHeight: 0,
      previousConversationSize: 0,
      previousLastMessageId: null,
    };
  },
  computed: {
    ...mapGetters({
      earliestMessage: 'conversation/getEarliestMessage',
      lastMessage: 'conversation/getLastMessage',
      allMessagesLoaded: 'conversation/getAllMessagesLoaded',
      isFetchingList: 'conversation/getIsFetchingList',
      conversationSize: 'conversation/getConversationSize',
      isAgentTyping: 'conversation/getIsAgentTyping',
      conversationAttributes: 'conversationAttributes/getConversationParams',
    }),
    colorSchemeClass() {
      return `${this.darkMode === 'dark' ? 'dark-scheme' : 'light-scheme'}`;
    },
    showStatusIndicator() {
      const { status } = this.conversationAttributes;
      const isConversationInPendingStatus = status === 'pending';
      const isLastMessageIncoming =
        this.lastMessage.message_type === MESSAGE_TYPE.INCOMING;
      return (
        this.isAgentTyping ||
        (isConversationInPendingStatus && isLastMessageIncoming)
      );
    },
  },
  watch: {
    allMessagesLoaded() {
      this.previousScrollHeight = 0;
    },
  },
  mounted() {
    this.$el.addEventListener('scroll', this.handleScroll);
    this.scrollToBottom();
  },
  updated() {
    const currentLastMessageId = this.lastMessage?.id;
    const hasNewMessage =
      this.previousConversationSize !== this.conversationSize ||
      this.previousLastMessageId !== currentLastMessageId;

    if (hasNewMessage) {
      this.previousConversationSize = this.conversationSize;
      this.previousLastMessageId = currentLastMessageId;
      // Scroll after DOM update
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    }
  },
  unmounted() {
    this.$el.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapActions('conversation', ['fetchOldConversations']),
    scrollToBottom() {
      const container = this.$el;
      const targetScrollTop =
        container.scrollHeight - this.previousScrollHeight;

      if (this.previousScrollHeight > 0) {
        // When loading old messages, jump instantly to preserve position
        container.scrollTop = targetScrollTop;
      } else {
        // For new messages, scroll smoothly
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      }
      this.previousScrollHeight = 0;
    },
    handleScroll() {
      if (
        this.isFetchingList ||
        this.allMessagesLoaded ||
        !this.conversationSize
      ) {
        return;
      }

      if (this.$el.scrollTop < 100) {
        this.fetchOldConversations({ before: this.earliestMessage.id });
        this.previousScrollHeight = this.$el.scrollHeight;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';
@import '~widget/assets/scss/mixins.scss';

.conversation--container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  color-scheme: light dark;

  &.light-scheme {
    color-scheme: light;
  }
  &.dark-scheme {
    color-scheme: dark;
  }
}

.conversation-wrap {
  flex: 1;
  padding: $space-large $space-small $space-small $space-small;
}

.message--loader {
  text-align: center;
}
</style>

<style lang="scss">
.message-slide-up-enter-active,
.message-slide-up-appear-active {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}

.message-slide-up-enter,
.message-slide-up-enter-from,
.message-slide-up-appear,
.message-slide-up-appear-from {
  opacity: 0;
  transform: translateY(8px);
}

.message-slide-up-enter-to,
.message-slide-up-appear-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
